import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateEffect } from '@private/hooks';
import { i18n } from 'i18next';

import Accordion from 'components/Accordion';
import { LOCAL_SEARCH_KEY, SHOW_CHILD_ENTITIES } from 'constants/common';
import { appReactMemo } from 'hocs';
import { IFilterSetting, INullable } from 'utils/types';

import { useFilterContext } from '../../../FilterContext';
import { isAppliedFilterActive } from '../../../utils/helpers';
import { FILTER_OPERATIONS, IAppliedFilter } from '../../../utils/types';
import FilterItem from '../FilterItem';
import SectionHeader from '../SectionHeader';

interface ICheckFilterAvailabilityProps {
  columnKey: string;
  i18n: i18n;
  ignoreList?: string[];
  intlPrefix?: string;
  listOperations: FILTER_OPERATIONS[];
}

interface ISection {
  filtrationResultsHandler: (state: INullable<boolean>) => void;
  hasFiltrationResults: MutableRefObject<INullable<boolean>>;
  isInitialOpen?: boolean;
  isLast: boolean;
  searchTerm?: string;
  sectionKey: string;
  sectionList: Map<string, IFilterSetting>;
}

const checkFilterAvailability = ({
  columnKey,
  i18n,
  ignoreList,
  intlPrefix = '',
  listOperations,
}: ICheckFilterAvailabilityProps): boolean => {
  const hasOperations = !!listOperations.length;
  const isI18nExist = i18n.exists(`${intlPrefix}.${columnKey}`);
  const isLocalSearchKey = columnKey === LOCAL_SEARCH_KEY;
  const isColumnIgnored = ignoreList?.some(
    (ignoredFilter) => ignoredFilter === columnKey,
  );

  return hasOperations && isI18nExist && !isLocalSearchKey && !isColumnIgnored;
};

interface IAppliedFiltersCount {
  active: number;
  total: number;
}

interface ICountAppliedFiltersProps {
  columnFilters: [string, IFilterSetting][];
  sidebarFilters: IAppliedFilter;
}

const countAppliedFilters = ({
  columnFilters,
  sidebarFilters,
}: ICountAppliedFiltersProps): IAppliedFiltersCount => {
  let total = 0;
  let active = 0;

  columnFilters.forEach(([filterKey]) => {
    const filter = sidebarFilters[filterKey];
    const filterValue = filter?.value;
    const isFilterValueDefined =
      filterValue !== undefined && filterValue !== null && filterValue !== '';

    if (isFilterValueDefined && filter) {
      total += 1;
      if (isAppliedFilterActive(filter)) {
        active += 1;
      }
    }
  });

  return { active, total };
};

const Section = ({
  filtrationResultsHandler,
  hasFiltrationResults,
  isInitialOpen = false,
  isLast,
  searchTerm,
  sectionKey,
  sectionList,
}: ISection) => {
  const { i18n, t } = useTranslation();

  const [isOpen, setIsOpen] = useState(isInitialOpen);
  const filtersNamesSet = useRef<Set<string>>(undefined);

  const {
    config: { defaultLikesFirstFields, ignoreList, intlPrefix },
    focusedFieldName,
    sidebarFilters,
  } = useFilterContext();

  useUpdateEffect(() => {
    if (focusedFieldName) {
      setIsOpen(!!filtersNamesSet.current?.has(focusedFieldName.columnKey));
    }
  }, [focusedFieldName]);

  const columnFilters = useMemo(() => {
    const namesCache = new Set<string>();

    const list = Array.from(sectionList.entries()).reduce<[string, IFilterSetting][]>(
      (prev, [columnKey, filter]) => {
        const { listOperations } = filter;

        const matchSearchTerm = searchTerm
          ? (t(`${intlPrefix}.${columnKey}` as never) as string)
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          : true;

        const isFilterAvailable = checkFilterAvailability({
          columnKey,
          i18n,
          ignoreList,
          intlPrefix,
          listOperations,
        });

        if (!isFilterAvailable || !matchSearchTerm) {
          return prev;
        }

        namesCache.add(columnKey);

        if (columnKey === SHOW_CHILD_ENTITIES) {
          prev.unshift([columnKey, filter]);
        } else {
          prev.push([columnKey, filter]);
        }

        return prev;
      },
      [],
    );

    filtersNamesSet.current = namesCache;

    return list;
  }, [sectionList, searchTerm, t, intlPrefix, ignoreList, i18n]);

  useUpdateEffect(() => {
    if (columnFilters.length && searchTerm) {
      hasFiltrationResults.current = true;
      return setIsOpen(true);
    }

    if (columnFilters.length && !searchTerm && !isInitialOpen) {
      return setIsOpen(false);
    }
  }, [columnFilters.length, searchTerm]);

  useUpdateEffect(() => {
    if (isLast && hasFiltrationResults.current === null && !columnFilters.length) {
      filtrationResultsHandler(false);
    }
  }, [isLast, columnFilters.length]);

  const appliedFiltersCounter = useMemo(
    () => countAppliedFilters({ columnFilters, sidebarFilters }),
    [sidebarFilters, columnFilters],
  );

  const openHandler = useCallback(
    (state?: boolean) => {
      setIsOpen(state ?? !isOpen);
    },
    [isOpen],
  );

  if (!columnFilters.length) {
    return null;
  }

  return (
    <Accordion
      header={(props) => (
        <SectionHeader
          {...props}
          activeCount={appliedFiltersCounter.active}
          openHandler={openHandler}
          sectionKey={sectionKey}
          totalCount={appliedFiltersCounter.total}
        />
      )}
      isInitialOpen={isInitialOpen}
      isOpen={isOpen}
    >
      {columnFilters.map(([columnKey, columnFilter]) => (
        <FilterItem
          columnKey={columnKey}
          defaultLikesFirstFields={defaultLikesFirstFields}
          key={columnKey}
          searchTerm={searchTerm}
          {...columnFilter}
        />
      ))}
    </Accordion>
  );
};

export default appReactMemo(Section);
